import React from "react";
import LegalPageShell from "./LegalPageShell";
import {
  BEENTARA_PRODUCT_SUMMARY,
  LEGAL_PRIVACY_EMAIL,
  LEGAL_SITE_URL,
} from "./legalContent";

export default function TermsConditions() {
  return (
    <LegalPageShell title="Terms & Conditions">
      <h2>1. Agreement to terms</h2>
      <p>
        These Terms &amp; Conditions (“Terms”) govern your access to and use of{" "}
        <strong>Beentara</strong>, including the website at{" "}
        <a href={LEGAL_SITE_URL} target="_blank" rel="noopener noreferrer">
          beentara.com
        </a>
        , the admin panel, mobile or web interfaces, APIs, and related services (the “Platform”).
      </p>
      <p>
        By registering, logging in, or using the Platform, you agree to these Terms. If you do not
        agree, do not use Beentara.
      </p>

      <h2>2. Description of service</h2>
      <p>{BEENTARA_PRODUCT_SUMMARY}</p>
      <p>
        Features available to you may depend on your subscription plan, business category, and
        account permissions. We may add, modify, or discontinue features with reasonable notice where
        practicable.
      </p>

      <h2>3. Eligibility and accounts</h2>
      <ul>
        <li>You must be at least 18 years old and authorised to bind your business or organisation.</li>
        <li>
          You must provide accurate registration information and keep your account details up to date.
        </li>
        <li>
          You are responsible for all activity under your account, including actions by staff or
          sub-users you invite.
        </li>
        <li>
          You must safeguard login credentials and enable two-step verification where offered. Notify
          us promptly of unauthorised access.
        </li>
      </ul>

      <h2>4. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Platform for unlawful, fraudulent, or harmful purposes.</li>
        <li>Upload malware, attempt unauthorised access, or interfere with Platform security or performance.</li>
        <li>Reverse engineer, scrape, or copy the Platform except as permitted by law.</li>
        <li>
          Use the Platform to send spam, harass others, or process data without a lawful basis or
          required consents.
        </li>
        <li>Misrepresent your identity or your relationship with Beentara.</li>
        <li>Resell or sublicense the Platform without our written permission.</li>
      </ul>
      <p>
        We may suspend or terminate access if we reasonably believe you have violated these Terms or
        pose a risk to the Platform or other users.
      </p>

      <h2>5. Your business data</h2>
      <p>
        You retain ownership of data you submit to Beentara (“Customer Data”), including customer
        records, orders, invoices, CRM leads, inventory, admissions, and files you upload.
      </p>
      <p>
        You grant us a limited licence to host, process, back up, and display Customer Data solely
        to provide and improve the Platform, comply with law, and support you. You represent that you
        have all rights and permissions needed to submit Customer Data and that its use through
        Beentara does not violate third-party rights or applicable law.
      </p>

      <h2>6. Subscriptions, fees, and payments</h2>
      <ul>
        <li>
          Paid plans, if applicable, are billed according to the pricing shown at signup or in your
          agreement with Beentara.
        </li>
        <li>Fees are generally non-refundable except where required by law or expressly stated.</li>
        <li>
          You authorise us and our payment partners to charge applicable fees, taxes, and renewals.
        </li>
        <li>
          Failure to pay may result in suspension or downgrade of service after reasonable notice.
        </li>
      </ul>
      <p>
        Payment processing for your end customers (orders, admissions, invoices, etc.) may be
        handled through third-party gateways; their terms apply to those transactions.
      </p>

      <h2>7. Intellectual property</h2>
      <p>
        Beentara, its logos, software, design, documentation, and all related intellectual property
        are owned by us or our licensors. These Terms do not grant you any ownership rights—only a
        limited, non-exclusive, non-transferable right to use the Platform during your subscription
        or authorised access period.
      </p>

      <h2>8. Third-party services</h2>
      <p>
        The Platform may integrate with third-party tools (payment providers, maps, messaging, cloud
        storage, etc.). We are not responsible for third-party services, their availability, or
        their terms. Your use of integrations is at your own risk.
      </p>

      <h2>9. Service availability and support</h2>
      <p>
        We strive to keep Beentara available and secure but do not guarantee uninterrupted or
        error-free operation. Maintenance, updates, or events beyond our control may cause temporary
        downtime. Support channels and response times may vary by plan.
      </p>

      <h2>10. Disclaimer of warranties</h2>
      <p>
        To the fullest extent permitted by law, the Platform is provided “as is” and “as available”
        without warranties of any kind, whether express or implied, including merchantability,
        fitness for a particular purpose, and non-infringement. We do not warrant that the Platform
        will meet your specific business requirements or that data entered by you or your users will
        always be accurate.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by applicable law, Beentara and its affiliates, directors,
        employees, and partners shall not be liable for any indirect, incidental, special,
        consequential, or punitive damages, or for loss of profits, revenue, data, or goodwill, arising
        from your use of the Platform.
      </p>
      <p>
        Our total aggregate liability for claims relating to the Platform shall not exceed the
        greater of (a) the fees you paid to Beentara in the twelve (12) months before the claim, or
        (b) INR 10,000, except where liability cannot be limited under applicable law.
      </p>

      <h2>12. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless Beentara from claims, damages, losses, and expenses
        (including reasonable legal fees) arising from your use of the Platform, your Customer Data,
        your violation of these Terms, or your violation of any law or third-party rights.
      </p>

      <h2>13. Termination</h2>
      <p>
        You may stop using the Platform at any time. We may suspend or terminate your account for
        breach of these Terms, non-payment, legal requirements, or extended inactivity, with notice
        where reasonable.
      </p>
      <p>
        Upon termination, your right to access the Platform ends. Provisions that by nature should
        survive (including intellectual property, disclaimers, limitation of liability, and
        indemnification) will remain in effect.
      </p>

      <h2>14. Governing law and disputes</h2>
      <p>
        These Terms are governed by the laws of India, without regard to conflict-of-law principles.
        Courts in India shall have exclusive jurisdiction over disputes arising from these Terms or
        the Platform, subject to any mandatory consumer protection laws that apply to you.
      </p>

      <h2>15. Changes to these terms</h2>
      <p>
        We may update these Terms from time to time. The “Last updated” date at the top indicates
        the current version. Continued use after changes become effective constitutes acceptance. For
        material changes, we may provide additional notice through the Platform or email.
      </p>

      <h2>16. Contact</h2>
      <p>For questions about these Terms, contact:</p>
      <ul>
        <li>
          Email: <a href={`mailto:${LEGAL_PRIVACY_EMAIL}`}>{LEGAL_PRIVACY_EMAIL}</a>
        </li>
        <li>
          Website:{" "}
          <a href={LEGAL_SITE_URL} target="_blank" rel="noopener noreferrer">
            {LEGAL_SITE_URL}
          </a>
        </li>
      </ul>
    </LegalPageShell>
  );
}
